/**
 * Dragging a document into a folder: the parts with no DOM in them.
 *
 * TASK-042 §6. The gesture itself lives in `DocList.svelte`, `FolderRail` and
 * `FolderBranch`, because it is `dragstart` / `dragover` / `drop` on elements.
 * Everything that DECIDES anything is here, so that it can be tested without a
 * browser -- which matters more than usual for this feature, because the three
 * things most likely to be got wrong are all arithmetic or string mapping:
 *
 *  1. **The capacity pre-check is `docCount + subfolders`, never `docCount`.**
 *     `Folder.docCount` is this person's non-archived placements and nothing
 *     else; the server's 100-object limit counts documents AND subfolders
 *     (`countChildren` sums `folders WHERE parent_id = $1` and
 *     `doc_placements WHERE folder_id = $1`). A browser comparing `docCount`
 *     alone against 100 paints a folder holding 95 documents and 5 subfolders
 *     as a perfectly good drop target and then watches the server refuse the
 *     drop it just invited.
 *
 *  2. **A refusal is told apart by the SHAPE OF ITS `data`, not by its code.**
 *     Three different refusals this screen can reach all carry `exists`: a
 *     full folder, a sibling name collision, and a cycle. Reading `exists` as
 *     "that folder is full" would tell somebody their folder held 100 of 100
 *     things because they dropped a folder onto its own child. `count` present
 *     means full, `name` present means collision, `folderId`/`parentId` alone
 *     means cycle -- and when none of them matches, the server's own `message`
 *     is the only honest answer to a refusal this browser has not been taught.
 *
 *  3. **A refused drop reverts OUT LOUD.** The silent snap-back is the failure
 *     this whole section exists to design against, so the sentence names three
 *     things: what came back, where it came back to, and why.
 *
 * The local pre-check is a COURTESY and never a fence. The counts are stale
 * the moment another tab or an agent writes, so the server stays the
 * authority: every message below is reachable from a real refusal as well as
 * from the pre-check, and `performDrop` sends the request whenever the
 * pre-check passes.
 */

/**
 * The payload's MIME type, and an `{ids: […]}` ARRAY from day one.
 *
 * Multi-select is out of v1 (§6e) and v1 drags exactly one document -- but the
 * protocol is the shape it will need, so that adding a selection later is a
 * change to what fills the array rather than a change to what the array is.
 */
export const DRAG_MIME = 'application/x-unomena-docs'

/**
 * Decision A (TASK-042 §6b): the left tree accepts a dropped document.
 *
 * The user was explicit that actions belong on the right, and this ticket
 * reads that as *no action buttons and no menus* -- the things §2 stripped out
 * of the rail. A drop target is neither: it adds no control, no label and
 * nothing to click, and the tree looks exactly as it does with this off until
 * something is already being dragged over it. It is also the only way to move
 * a document into a folder that is not a child of the one you are looking at,
 * without first navigating away from the document you are holding.
 *
 * It is a constant rather than an inline `true` so that turning it off is one
 * word here: `false` passes `undefined` for `FolderRail`'s `zone` prop, the
 * tree becomes inert, and nothing else in the screen changes.
 */
export const TREE_ACCEPTS_DROPS = true

/**
 * Mirrors `MAX_FOLDER_CHILDREN` in `packages/server/src/service/folders.ts`.
 *
 * Duplicated deliberately and narrowly: the browser needs a number BEFORE the
 * request to mark a target non-droppable, and the number the person is shown
 * on a real refusal comes from the refusal's own `data.limit` rather than from
 * here (§6d: "TASK-039 §4b puts the limit in `detail` and `data` precisely so
 * the number is not hardcoded in two places"). If they ever disagree, the
 * server wins and the only cost is one round trip.
 */
export const MAX_FOLDER_CHILDREN = 100

export interface DragPayload {
  ids: string[]
}

export function encodeDrag(ids: string[]): string {
  return JSON.stringify({ ids } satisfies DragPayload)
}

/**
 * Read a payload back, and treat ANYTHING unexpected as "not one of ours".
 *
 * A drop handler is reachable by anything a person can drag -- a file from the
 * desktop, a selection from another tab, a link -- so this returns an empty
 * array rather than throwing, and the caller does nothing with an empty array.
 */
export function decodeDrag(raw: string | null | undefined): string[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (!parsed || !Array.isArray(parsed.ids)) return []
    return parsed.ids.filter((id: unknown): id is string => typeof id === 'string' && id !== '')
  } catch {
    return []
  }
}

/**
 * Is this drag one of ours?
 *
 * `dataTransfer.types` is readable during `dragover` while the DATA is not,
 * which is exactly why the payload has a custom MIME type: a target has to
 * decide whether to accept before it is allowed to look inside. Anything else
 * a person can drag -- a file from the desktop, a link from another tab -- is
 * simply not ours, and a target that did not check would swallow it.
 */
export function isDocDrag(event: { dataTransfer?: DataTransfer | null }): boolean {
  const types = event.dataTransfer?.types
  return Boolean(types && Array.prototype.includes.call(types, DRAG_MIME))
}

/**
 * Tell the browser this element is a drop target, and what dropping means.
 *
 * `preventDefault()` on `dragover` is the whole of "yes, you may drop here",
 * and it is also what makes the `drop` event fire on US rather than on
 * something behind us. A REFUSING target still calls it, with
 * `dropEffect: 'none'`: the cursor says no during the drag, and the drop still
 * lands here so that letting go can say why rather than doing nothing.
 */
export function acceptDrag(event: DragEvent, accepted: boolean): boolean {
  if (!isDocDrag(event)) return false
  event.preventDefault()
  if (event.dataTransfer) event.dataTransfer.dropEffect = accepted ? 'move' : 'none'
  return true
}

/**
 * Has the pointer actually LEFT this target, or only moved onto a child of it?
 *
 * `dragleave` fires on the row every time the pointer crosses into one of its
 * own children -- the icon, the name, the count -- so a handler that cleared
 * the highlight on every `dragleave` would flicker the affordance off and on
 * as somebody moved across the row they were aiming at. The `relatedTarget` is
 * what the pointer moved TO; if the row still contains it, nothing has been
 * left.
 */
export function leftTarget(event: DragEvent): boolean {
  const from = event.currentTarget
  const to = event.relatedTarget
  if (!(from instanceof Element)) return true
  if (!(to instanceof Node)) return true
  return !from.contains(to)
}

/** The ids a drop carried, or none. */
export function docIdsFrom(transfer: DataTransfer | null | undefined): string[] {
  if (!transfer) return []
  return decodeDrag(transfer.getData(DRAG_MIME))
}

/**
 * What a drop target needs from the screen, as ONE object.
 *
 * The rail threads it through a recursive component, so it is one prop rather
 * than four -- and because it is one prop, decision A (§6b: "confined to phase
 * 4 and gated behind one prop on `FolderRail`") is turned off by passing
 * `undefined`, which is the one-word change `TREE_ACCEPTS_DROPS` makes.
 */
export interface DropZone {
  /** A document drag is in progress. Nothing about a target changes at rest. */
  active: boolean
  /** May this place take it? `null` is the root. */
  accepts: (id: string | null) => boolean
  /** Pointed at during a drag, and `false` on leaving. Drives the announcement. */
  aim: (id: string | null, over: boolean) => void
  /** Let go over this place. */
  drop: (id: string | null, transfer: DataTransfer | null) => void
}

/* ------------------------------------------------------- the pre-check */

/** What the SERVER counts in a folder: its documents and its subfolders. */
export function folderLoad(docCount: number, subfolders: number): number {
  return docCount + subfolders
}

export function folderIsFull(docCount: number, subfolders: number): boolean {
  return folderLoad(docCount, subfolders) >= MAX_FOLDER_CHILDREN
}

export interface DropTarget {
  /** `null` is the root, which is a place without a row (§1). */
  id: string | null
  /** What a sentence calls it: a folder's name, or `ROOT_NAME`. */
  name: string
  /** This person's non-archived placements in it. Meaningless for the root. */
  docCount: number
  /** Its direct subfolders, from the tree the browser already built. */
  subfolders: number
}

export type DropVerdict =
  | { ok: true }
  /** It is already there, or the folder is full. Both say so; neither is silent. */
  | { ok: false; why: 'already' | 'full'; message: string }

/**
 * May this document be dropped here, as far as the browser can tell?
 *
 * **The root has no cap, and that is not an oversight.** The server counts the
 * children of a FOLDER; unfiling a document deletes its placement row rather
 * than adding an object to anything, so `placeDoc(null)` has no count check to
 * fail. A root shown as full would be a browser refusing a call the server
 * would have accepted.
 */
export function canDrop(target: DropTarget, from: string | null, docName: string): DropVerdict {
  if (target.id === from) {
    return {
      ok: false,
      why: 'already',
      message: `“${docName}” is already in ${target.name}.`,
    }
  }
  if (target.id !== null && folderIsFull(target.docCount, target.subfolders)) {
    return {
      ok: false,
      why: 'full',
      message: fullSentence(target.name, folderLoad(target.docCount, target.subfolders)),
    }
  }
  return { ok: true }
}

/** One sentence, used by the pre-check and by the server's own refusal. */
export function fullSentence(name: string, count: number, limit = MAX_FOLDER_CHILDREN): string {
  return `${name} is full: it holds ${count} of ${limit} items. Move something out of it first, or file this somewhere else.`
}

/* --------------------------------------------- what a refusal MEANS */

/** As much of a refusal as this module reads. `ApiError` satisfies it. */
export interface RefusalLike {
  code?: string | null
  path?: string[]
  detail?: string
  message?: string
  body?: { data?: Record<string, any> | null; message?: string } | null
}

function dataOf(err: RefusalLike): Record<string, any> {
  const data = err.body?.data
  return data && typeof data === 'object' ? data : {}
}

/** The server's own sentence, when this browser has not been taught the refusal. */
function serverSaid(err: RefusalLike): string {
  const said = err.detail || err.body?.message || err.message || ''
  const trimmed = String(said).trim()
  if (!trimmed) return 'The server refused the move.'
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`
}

/**
 * Why the move was refused, in a sentence naming the obstacle and the remedy.
 *
 * Branch on `code`, then on the SHAPE of `data` -- see the header comment.
 * `targetName` is what the person aimed at, because the refusal's own `data`
 * carries ids and counts but never a name a person would recognise.
 */
export function refusalMessage(err: unknown, targetName: string): string {
  if (!err || typeof err !== 'object') return 'The server refused the move.'
  const refusal = err as RefusalLike
  const data = dataOf(refusal)
  const code = refusal.code ?? null

  if (code === 'exists') {
    // Full: `{count, limit, adding}` from `tooManyChildren`.
    if (typeof data.count === 'number') {
      const limit = typeof data.limit === 'number' ? data.limit : MAX_FOLDER_CHILDREN
      return fullSentence(targetName, data.count, limit)
    }
    // Sibling name collision: `{name}` from `nameTaken`. Only reachable by
    // dragging a FOLDER, which is out of scope for this pass -- it is mapped
    // anyway, because §6f is what turns it on and leaving a refusal to fall
    // through to a raw server sentence is how a feature ships half-taught.
    if (typeof data.name === 'string') {
      return `There is already a folder called “${data.name}” there.`
    }
    // A cycle: `{folderId}` for the self case, `{folderId, parentId}` for the
    // descendant case. The LAST of the three, because the other two are told
    // apart by fields a cycle never carries.
    if (typeof data.folderId === 'string' || typeof data.parentId === 'string') {
      return 'You cannot put a folder inside itself.'
    }
    return serverSaid(refusal)
  }

  if (code === 'unknown_key' && typeof data.limit === 'number') {
    return `That would nest too deep. Folders go ${data.limit} deep and this move would make ${data.limit + 1}. Move the folder higher up first.`
  }

  // A folder that is gone, or was never this person's: `access` with the path
  // `['ref','folderId']`. `isMissingError` matches exactly this, so it MUST
  // NOT reach the sign-in redirect -- a stale tree is an ordinary event in a
  // file browser and the answer to it is a sentence, not a sign-in box.
  if (code === 'access' && (refusal.path ?? [])[0] === 'ref') {
    return `${targetName} is gone, or was never yours. Your folders have been refreshed.`
  }

  return serverSaid(refusal)
}

/**
 * The whole sentence a refused drop puts in `actionError`.
 *
 * Three things, because a silent snap-back is the failure to design against
 * and a message that names only one of them is most of the way back to it:
 * WHAT came back, WHERE it came back to, and WHY.
 */
export function revertSentence(docName: string, backIn: string, because: string): string {
  return `“${docName}” is back in ${backIn}. ${because}`
}

/* ------------------------------------------------------------ the queue */

/**
 * Drops run ONE AT A TIME (§6d step 2).
 *
 * Two optimistic moves into the same nearly-full folder can both pass a local
 * count check, and the second one has to be allowed to fail honestly against
 * the server rather than being waved through by a count the first move has not
 * yet changed. Serialising is also what keeps the revert simple: at most one
 * pending record exists at a time, so a refusal cannot restore a snapshot that
 * a later drop has already built on.
 *
 * A rejection does not break the chain -- the next drop still runs -- because
 * one refused move is not a reason to stop accepting the next one.
 */
export function serialise(): (job: () => Promise<void>) => Promise<void> {
  let tail: Promise<void> = Promise.resolve()
  return (job) => {
    const next = tail.then(job, job)
    tail = next.then(
      () => {},
      () => {},
    )
    return next
  }
}
